import { Trans } from 'react-i18next'

import { LANGUAGE_OPTIONS, LOCALE_OPTIONS } from '@makyoapp/makyo-common'
import { Moon24, Sun24, SunMoon24 } from '@makyoapp/makyo-icon/icon'
import { CountryOption, JustifyVariant, Modal, SelectDropdown, SingleIconSelect, ThemeMode } from '@makyoapp/makyo-ui'

import { LocaleDd } from '@/components/LocaleDd'
import { RowField, RowLabel, RowValue } from '@/components/ProfileRow'
import { TzDd } from '@/components/TzDd'
import { UploadUserAvatar } from '@/components/UploadAvatar'

import useMyProfile from '../hooks/useMyProfile'

const MyProfilePanel: React.FunctionComponent = () => {
  const now = new Date()
  const {
    t,
    handleUpdateProfile,
    setSelectedLocale,
    setSelectedTz,
    setSelectedLang,
    setPrintLocale,
    email,
    currentName,
    localeFmt,
    printLocale,
    selectedLang,
    userData,
    selectedTz,
    themeState,
    selectedLocale,
  } = useMyProfile()

  return (
    <div className='grid grid-cols-1 lg:flex gap-8'>
      <div>
        {/* Render avatar upload component */}
        <UploadUserAvatar />
      </div>
      <div className='flex flex-col gap-12 md:w-[320px]'>
        <div>
          {/* Render user name component */}
          <RowLabel label={t('Name')} />
          <div>
            <RowValue
              value={currentName || 'N/A'}
              canEdit={true}
              id={'my-profile-name'}
              validate={(newValue, setError) => {
                //const regex = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,<>\/?~]+/gim
                const trimmed = newValue.trim()
                const valid = Boolean(Boolean(trimmed) && trimmed.length > 1 && trimmed !== userData?.name?.trim())

                if (!valid) {
                  setError(t('Invalid Name'))
                } else {
                  setError('')
                }
                return valid
              }}
              onEdit={(newValue) => {
                if (newValue && newValue.trim() !== userData?.name?.trim()) {
                  handleUpdateProfile({
                    name: newValue,
                  })
                }
              }}
            />
          </div>
        </div>
        {/* Render email component */}
        <div>
          <RowLabel label={t('Email')} />
          <div>
            <RowValue value={email || ''} />
          </div>
        </div>
        {/* Render Locale component */}
        <div>
          <RowLabel label={t('Locale')} />
          <div>
            <RowField>
              <LocaleDd
                id='my-profile-locale-dd'
                withSearch={true}
                enableAutoScroll={true}
                value={selectedLocale}
                onChange={(opt) => {
                  setSelectedLocale(opt)
                  handleUpdateProfile({
                    locale: opt,
                  })
                }}
              />
            </RowField>
            {/* Locale formatting and formatting modal */}
            <div className='mt-2 flex-c w-full min-w-0 justify-between'>
              <span>{localeFmt.format(now)}</span>
              <button
                className='link'
                onClick={() => {
                  setPrintLocale(true)
                }}
              >
                <Trans>View Formats</Trans>
              </button>
              {printLocale && (
                <Modal show={true} onHide={() => setPrintLocale(false)}>
                  <Modal.Header onCloseClick={() => setPrintLocale(false)}>
                    <Trans>Locale List</Trans>
                  </Modal.Header>
                  <Modal.Body className='max-h-[50vh] overflow-y-auto'>
                    {LOCALE_OPTIONS.map((opt, i) => {
                      const fmt = Intl.DateTimeFormat(opt.value, {
                        dateStyle: 'short',
                        timeStyle: 'medium',
                      })
                      return (
                        <div key={i} className='flex-c'>
                          <div className='min-w-[300px]'>
                            {opt.label} ({opt.value})
                          </div>
                          <div>
                            {fmt.format(now)}
                            {/*
                                     <span>With Sec </span>
                                     <span>
                                     {fmtDt(now, opt.value, {
                                     dtDisplay: 'datetime',
                                     dtDateFormatType: 'fixed',
                                     dtLocale: opt.value,
                                     dtDateFormat: 'medium',
                                     dtDisplayTz: true,
                                     dtDisplaySecond: true,
                                     dtTimeFormat: 'ampm',
                                     dtTimezone: 'auto'
                                     })}
                                     </span>
                                     </div>
                                     <div>
                                     <span>No Sec </span>
                                     <span>
                                     {fmtDt(now, opt.value, {
                                     dtDisplay: 'datetime',
                                     dtDateFormatType: 'fixed',
                                     dtLocale: opt.value,
                                     dtDateFormat: 'medium',
                                     dtDisplayTz: true,
                                     dtDisplaySecond: false,
                                     dtTimeFormat: 'ampm',
                                     dtTimezone: 'auto'
                                     })}
                                     </span>
                                     */}
                          </div>
                        </div>
                      )
                    })}
                  </Modal.Body>
                </Modal>
              )}
            </div>
          </div>
        </div>
        <div>
          {/* Render the Timezone label */}
          <RowLabel label={t('Timezone')} />
          <div>
            <RowField>
              {/* Render the Timezone dropdown menu. When an option is selected, the timezone is set and the user profile is updated */}
              <TzDd
                id='my-profile-tz-dd'
                value={selectedTz}
                onChange={(opt) => {
                  setSelectedTz(opt)
                  handleUpdateProfile({
                    timezone: opt,
                  })
                }}
              />
            </RowField>
          </div>
        </div>
        <div>
          {/* Render the Language label */}
          <RowLabel label={t('Language')} />
          <div>
            <RowField>
              {/* Render the Language dropdown menu. When an option is selected, the language is set and the user profile is updated */}
              <SelectDropdown
                id='my-profile-lang-dd'
                value={selectedLang}
                onChange={(opt) => {
                  setSelectedLang(opt)
                  handleUpdateProfile({
                    lang: opt.value,
                  })
                }}
                renderOption={(opt) => {
                  return <CountryOption slug={opt.country} name={opt.label} />
                }}
                optionLabel={(opt) => opt.label}
                options={LANGUAGE_OPTIONS}
              />
            </RowField>
          </div>
        </div>
        <div>
          <RowField>
            {/* Render the Theme switch. When toggled, the theme mode (dark or light) is set and the user profile is updated */}
            <RowLabel label={t('Theme')} />
            <SingleIconSelect
              id='my-profile-mode-switch'
              justify={JustifyVariant.End}
              onClick={(e) => {
                if (e.id === themeState.mode) {
                  return
                }

                handleUpdateProfile({
                  theme: e.id as ThemeMode,
                })
              }}
              selected={themeState.mode}
              options={[
                {
                  icon: () => <Sun24 />,
                  title: t('Light'),
                  id: 'light',
                },
                {
                  icon: () => <Moon24 />,
                  title: t('Dark'),
                  id: 'dark',
                },
                {
                  icon: () => <SunMoon24 />,
                  title: t("Match device's setting"),
                  id: 'system',
                },
              ]}
            />
          </RowField>
        </div>
      </div>
    </div>
  )
}

export default MyProfilePanel
